import { Suspense, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Badge, Modal, ModalProps, Skeleton, Tabs } from 'antd';
import chatttSDK, { canNetScan, canBrowserSetting, canZoomView } from 'services/chattt';
import { useIsChatttUpdate, useIsFettUpdate } from 'hooks/useUpdater';
import { NetScan } from '../components/NetScanModal';
import ConversationSetting from '../ConversationSetting';
import { useTranslate } from '@tolgee/react';
import { ThemeColor } from '../components/ThemeColor';
import { ZoomView } from '../components/ZoomView';
import { SystemLanguage } from '../components/SystemLanguage';
import { BrowserSetting } from '../components/BrowserSetting';
import { DownloadPath } from '../components/DownloadPath';
import { QuitSetting } from '../components/QuitSetting';
import { OthersSetting } from '../components/OthersSetting';
import { RemindSetting } from '../components/RemindSetting';
import { ClientVersion } from '../components/ClientVersion';
import { TranslateLanguage } from '../components/TranslateLanguage';
import SimpleMode from '../components/SimpleMode';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import {
  useSetSystemModalActiveFeature,
  useSetSystemModalActiveKey,
  useSystemModalActiveKey,
} from 'hooks/useSystemModal';
import { eventBus } from 'services/eventBus';
import { ChatLayoutTypeSelect } from '../components/ChatLayoutTypeSelect';

interface SystemProps {
  onClose?: Function;
}

function System({ onClose }: SystemProps) {
  const { t } = useTranslate();

  const isChatttUpdate = useIsChatttUpdate();
  const isFettUpdate = useIsFettUpdate();

  const contextActiveKey = useSystemModalActiveKey();
  const setContextActiveKey = useSetSystemModalActiveKey();

  const [activeKey, setActiveKey] = useState(contextActiveKey || 'preference');

  const { whiteListConfig: { MESSAGE_SIMPLE_MODE } = {} } = useOrgConfigStateValue();

  const preference = (
    <Tabs.TabPane key="preference" tab={t('aide.偏好设置')}>
      <section className="min-h-full relative bg-fill-0">
        {chatttSDK?.version ? <RemindSetting /> : null}
        <ChatLayoutTypeSelect />
        <ThemeColor />
        {canZoomView && <ZoomView />}
        <SystemLanguage />
        <TranslateLanguage />
        {MESSAGE_SIMPLE_MODE && <SimpleMode />}
        {canBrowserSetting && <BrowserSetting />}
        {chatttSDK && <DownloadPath />}
        {chatttSDK?.version && <QuitSetting />}
        {chatttSDK && <OthersSetting />}
      </section>
    </Tabs.TabPane>
  );

  const conversation = (
    <Tabs.TabPane key="conversation" tab={t('nav.会话设置')}>
      <ConversationSetting onClose={onClose} />
    </Tabs.TabPane>
  );

  const netScan = canNetScan && (
    <Tabs.TabPane key="netScan" tab={t('ViewSystem.system.net.scan.title')}>
      <NetScan />
    </Tabs.TabPane>
  );

  const canUpdate = isChatttUpdate.value || isFettUpdate.value;

  const updateVersion = (
    <Tabs.TabPane key="updateVersion" tab={<Badge dot={canUpdate}>{t('version_update')}</Badge>}>
      <ClientVersion canUpdate={canUpdate} />
    </Tabs.TabPane>
  );

  const tabs = (
    <Tabs
      tabPosition="left"
      activeKey={contextActiveKey || activeKey}
      onChange={(activeKey) => {
        setActiveKey(activeKey);
        setContextActiveKey?.(activeKey);
      }}
    >
      {preference}
      {conversation}
      {netScan}
      {updateVersion}
    </Tabs>
  );

  return <Container>{tabs}</Container>;
}

export const SystemModal = (props: ModalProps) => {
  const { t } = useTranslate();
  return (
    <Modal title={t('ViewSystem.system.title.setting')} width={760} footer={null} {...props}>
      <Suspense fallback={<Skeleton />}>
        <System onClose={props.onCancel} />
      </Suspense>
    </Modal>
  );
};

export function SystemModalContextProvider(props: { children?: React.ReactNode }) {
  const { children } = props;
  const setActiveKey = useSetSystemModalActiveKey();
  const setActiveFeature = useSetSystemModalActiveFeature();

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handler = ({
      activeKey,
      activeFeature,
    }: {
      activeKey?: string;
      activeFeature?: string;
    }) => {
      setVisible(true);
      setActiveKey(activeKey);
      setActiveFeature(activeFeature);
    };
    eventBus.on('SHOW_SYSTEM_MODAL', handler);
    return () => {
      eventBus.off('SHOW_SYSTEM_MODAL', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {children}
      <SystemModal visible={visible} onCancel={() => setVisible(false)} />
    </>
  );
}

const Container = styled.div`
  margin: -24px;
  > .ant-tabs {
    max-height: calc(100vh - 200px);
    .ant-tabs-nav-list {
      padding: 12px 0;
    }
    .ant-tabs-content {
      height: 100%;
      .ant-tabs-tabpane {
        height: 100%;
        padding: 18px 18px 18px 28px;
        overflow: auto;
      }
    }
  }
`;
